import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// feexpayWebhook — configuration et supervision du webhook FeexPay.
//
// L'endpoint réel est une fonction serveur publique :
//   {SUPABASE_URL}/functions/v1/feexpay-webhook
// Il authentifie l'appel (Bearer), journalise TOUT ce qui arrive puis
// délègue la confirmation métier à `feexpay-callback`.
// ─────────────────────────────────────────────────────────────

export const FEEXPAY_WEBHOOK_SLUG = 'feexpay-webhook';
export const FEEXPAY_WEBHOOK_PRETTY_PATH = '/api/v1/payments/feexpay-webhook';
export const FEEXPAY_WEBHOOK_HEADER_NAME = 'Authorization';
export const FEEXPAY_WEBHOOK_HEADER_TYPE = 'Bearer';

/** URL publique réelle de la fonction serveur (à coller dans FeexPay). */
export function getFeexPayWebhookUrl(): string {
  const base = String(import.meta.env.VITE_PUBLIC_SUPABASE_URL || '').replace(/\/+$/, '');
  return base ? `${base}/functions/v1/${FEEXPAY_WEBHOOK_SLUG}` : '';
}

/** Alias lisible affiché à titre indicatif (nécessite une règle de proxy). */
export function getFeexPayWebhookPrettyUrl(): string {
  if (typeof window === 'undefined') return FEEXPAY_WEBHOOK_PRETTY_PATH;
  return `${window.location.origin}${FEEXPAY_WEBHOOK_PRETTY_PATH}`;
}

export interface WebhookEventOption {
  id: string;
  label: string;
  description: string;
  required: boolean;
}

/** Événements à cocher dans FeexPay → Webhooks. */
export const FEEXPAY_WEBHOOK_EVENTS: WebhookEventOption[] = [
  {
    id: 'payin.success',
    label: 'Paiement réussi',
    description: 'Confirme l’abonnement ou la commande boutique et crédite le parrain.',
    required: true,
  },
  {
    id: 'payin.failed',
    label: 'Paiement échoué / refusé',
    description: 'Marque la tentative comme échouée et alimente le journal des paiements.',
    required: true,
  },
  {
    id: 'payin.pending',
    label: 'Paiement en attente',
    description: 'Conserve la trace d’une demande envoyée (validation USSD en cours).',
    required: false,
  },
  {
    id: 'transaction.updated',
    label: 'Transaction mise à jour',
    description: 'Rattrape un changement de statut survenu après la confirmation initiale.',
    required: true,
  },
  {
    id: 'payout.success',
    label: 'Transfert sortant réussi',
    description: 'Journalisé pour information (aucune action métier).',
    required: false,
  },
  {
    id: 'payout.failed',
    label: 'Transfert sortant échoué',
    description: 'Journalisé pour information (aucune action métier).',
    required: false,
  },
];

export interface WebhookStatus {
  ok: boolean;
  secretConfigured: boolean;
  version: string | null;
  error: string | null;
}

function anonHeaders(): Record<string, string> {
  const key = String(import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY || '');
  return key ? { apikey: key } : {};
}

/** Interroge la fonction pour connaître son état (sans exposer le secret). */
export async function fetchFeexPayWebhookStatus(): Promise<WebhookStatus> {
  const url = getFeexPayWebhookUrl();
  if (!url) return { ok: false, secretConfigured: false, version: null, error: 'URL du backend introuvable.' };
  try {
    const response = await fetch(url, { method: 'GET', headers: anonHeaders() });
    const text = await response.text();
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      return { ok: false, secretConfigured: false, version: null, error: `Réponse inattendue (HTTP ${response.status}).` };
    }
    if (!response.ok || parsed.ok !== true) {
      return {
        ok: false,
        secretConfigured: false,
        version: null,
        error: String(parsed.error || parsed.reason || `HTTP ${response.status}`),
      };
    }
    return {
      ok: true,
      secretConfigured: parsed.secret_configured === true,
      version: parsed.version ? String(parsed.version) : null,
      error: null,
    };
  } catch (err) {
    return {
      ok: false,
      secretConfigured: false,
      version: null,
      error: err instanceof Error ? err.message : 'Fonction injoignable.',
    };
  }
}

export interface TestWebhookResult {
  ok: boolean;
  message: string;
  event?: string;
  logId?: number | null;
  raw?: string;
}

/** Envoie un faux webhook (mode test : rien n'est modifié en base). */
export async function sendFeexPayTestWebhook(): Promise<TestWebhookResult> {
  const url = getFeexPayWebhookUrl();
  if (!url) return { ok: false, message: 'URL du backend introuvable.' };

  const stamp = Date.now();
  const payload = {
    event: 'payin.success',
    data: {
      reference: `TEST-WEBHOOK-${stamp}`,
      callback_info: `TEST-WEBHOOK-${stamp}`,
      amount: 500,
      currency: 'XAF',
      status: 'success',
      operator: 'mtn',
      country: 'CG',
      customer: { phone: '242000000000' },
      transaction_id: `test-${stamp}`,
    },
    timestamp: new Date().toISOString(),
    test: true,
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        ...anonHeaders(),
        'Content-Type': 'application/json',
        'x-zifek-test': '1',
      },
      body: JSON.stringify(payload),
    });
    const text = await response.text();
    let parsed: Record<string, unknown> = {};
    try {
      parsed = JSON.parse(text) as Record<string, unknown>;
    } catch {
      return { ok: false, message: `Réponse illisible (HTTP ${response.status}).`, raw: text.slice(0, 600) };
    }
    if (!response.ok || parsed.ok !== true) {
      return {
        ok: false,
        message: String(parsed.reason || parsed.error || `HTTP ${response.status}`),
        raw: text.slice(0, 600),
      };
    }
    return {
      ok: true,
      message: String(parsed.message || 'Webhook reçu et journalisé.'),
      event: parsed.event ? String(parsed.event) : 'payin.success',
      logId: parsed.log_id != null ? Number(parsed.log_id) : null,
      raw: text.slice(0, 600),
    };
  } catch (err) {
    return {
      ok: false,
      message: err instanceof Error ? err.message : 'Envoi impossible.',
    };
  }
}

export interface WebhookDelivery {
  id: number;
  flow: string | null;
  status: string;
  amount: number | null;
  currency: string | null;
  operator: string | null;
  reference: string | null;
  http_status: number | null;
  endpoint: string | null;
  error_message: string | null;
  raw_response: string | null;
  request_payload: Record<string, unknown> | null;
  created_at: string;
}

/** Dernières livraisons FeexPay (webhook + callback), les plus récentes d'abord. */
export async function fetchFeexPayDeliveries(limit = 25): Promise<WebhookDelivery[]> {
  const { data, error } = await supabase
    .from('payment_attempt_logs')
    .select('id, flow, status, amount, currency, operator, reference, http_status, endpoint, error_message, raw_response, request_payload, created_at')
    .eq('provider', 'feexpay')
    .in('endpoint', ['feexpay-webhook', 'feexpay-callback'])
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as WebhookDelivery[];
}

/** Libellé + couleur d'un statut de livraison. */
export function deliveryBadge(status: string): { label: string; className: string; icon: string } {
  const key = (status || '').toLowerCase();
  if (key === 'received') return { label: 'Reçu', className: 'bg-green-50 text-green-700', icon: 'ri-check-double-line' };
  if (key === 'test') return { label: 'Test', className: 'bg-secondary-100 text-secondary-900', icon: 'ri-flask-line' };
  if (key === 'declined') return { label: 'Refusé', className: 'bg-amber-50 text-amber-700', icon: 'ri-close-circle-line' };
  if (key === 'rejected') return { label: 'Non autorisé', className: 'bg-red-50 text-red-600', icon: 'ri-shield-cross-line' };
  if (key === 'unmatched') return { label: 'Non apparié', className: 'bg-orange-50 text-orange-700', icon: 'ri-question-line' };
  if (key === 'failed') return { label: 'Échec', className: 'bg-red-50 text-red-600', icon: 'ri-error-warning-line' };
  if (key === 'sent') return { label: 'Envoyé', className: 'bg-background-100 text-foreground-600', icon: 'ri-send-plane-line' };
  return { label: status || '—', className: 'bg-background-100 text-foreground-600', icon: 'ri-information-line' };
}